import { useRef, useState } from "react";
import { IconChevronDown, IconDownload, IconFileImport, IconFileSpreadsheet } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Group,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { exportarExcel } from "@/shared/export/exportar-excel";
import { toastAdvertencia } from "@/lib/toast";
import type {
  FilaImportacionProveedor,
  PreviewFilaProveedor,
  ResultadoImportacionProveedores,
} from "../services/proveedores-service";
import { useImportarProveedores } from "../hooks/use-proveedores-mutations";
import { importarProveedores } from "../services/proveedores-service";

/** Encabezados aceptados por columna (normalizados: minúsculas, sin acentos ni separadores). */
const NUMERO_HEADERS = ["numerodeproveedor", "numeroproveedor", "numero", "numprov", "numeroprov", "noproveedor", "no", "num", "idproveedor"];
const RFC_HEADERS = ["rfc", "rfcproveedor"];
const RAZON_HEADERS = ["razonsocial", "razon", "nombre", "razonsocialproveedor", "proveedor"];
const BANCO_HEADERS = ["banco"];
const CUENTA_HEADERS = ["cuenta", "cuentabancaria", "numerodecuenta"];
const CLABE_HEADERS = ["clabe", "clabeinterbancaria"];
const TIPO_HEADERS = ["tipo", "tipoproveedor", "origen", "nacionalextranjero", "en", "tipodeproveedor"];

// Quita acentos también, para que "Número" (encabezado bonito de la plantilla) matchee "numero".
const norm = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[\s._-]/g, "");

function extraer(fila: Record<string, unknown>, candidatos: string[]): string {
  for (const [k, v] of Object.entries(fila)) {
    if (candidatos.includes(norm(k))) return String(v ?? "").trim();
  }
  return "";
}

const EJEMPLOS: FilaImportacionProveedor[] = [
  {
    numeroProveedor: "5001",
    rfc: "GFM980824UK0",
    razonSocial: "Proveedor nacional de ejemplo · reemplázame",
    banco: "BBVA",
    cuenta: "0123456789",
    clabe: "012180001234567890",
    tipo: "N",
  },
  {
    numeroProveedor: "5002",
    rfc: "109012",
    razonSocial: "Proveedor extranjero de ejemplo (E · sin RFC mexicano ni CLABE)",
    banco: "JPMorgan Chase",
    cuenta: "400238624",
    clabe: "",
    tipo: "E",
  },
];

/**
 * Control "Excel" unificado (importar + descargar plantilla) para el catálogo
 * de proveedores. Upsert por número de proveedor. Antes de persistir muestra
 * una VISTA PREVIA (dry-run) con qué se hará por fila (crear/actualizar/omitir)
 * · mismo patrón que los gestores de catálogos contables y clave→cuenta.
 */
export function ImportarExcelProveedores() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filas, setFilas] = useState<FilaImportacionProveedor[]>([]);
  const [preview, setPreview] = useState<ResultadoImportacionProveedores | null>(null);
  const importar = useImportarProveedores();

  function descargarPlantilla() {
    exportarExcel<FilaImportacionProveedor>({
      archivo: "plantilla-proveedores",
      hoja: "Proveedores",
      columnas: [
        { header: "Número de proveedor", value: (f) => f.numeroProveedor },
        { header: "Tipo (N/E)", value: (f) => f.tipo },
        { header: "RFC", value: (f) => f.rfc },
        { header: "Razón social", value: (f) => f.razonSocial },
        { header: "Banco", value: (f) => f.banco },
        { header: "Cuenta", value: (f) => f.cuenta },
        { header: "CLABE", value: (f) => f.clabe },
      ],
      filas: EJEMPLOS,
    });
  }

  const validar = useMutation({
    mutationFn: (fs: FilaImportacionProveedor[]) => importarProveedores(fs, true),
    onSuccess: (res) => setPreview(res),
    onError: () => toastAdvertencia({ title: "No se pudo validar el archivo", message: "Intenta de nuevo." }),
  });

  const cerrar = () => {
    setPreview(null);
    setFilas([]);
  };

  async function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        toastAdvertencia({ title: "Archivo vacío", message: "El Excel no tiene hojas con datos." });
        return;
      }
      const crudas = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
      const parsed: FilaImportacionProveedor[] = crudas
        .map((r) => ({
          numeroProveedor: extraer(r, NUMERO_HEADERS),
          rfc: extraer(r, RFC_HEADERS),
          razonSocial: extraer(r, RAZON_HEADERS),
          banco: extraer(r, BANCO_HEADERS),
          cuenta: extraer(r, CUENTA_HEADERS),
          clabe: extraer(r, CLABE_HEADERS),
          tipo: extraer(r, TIPO_HEADERS),
        }))
        .filter((f) => f.numeroProveedor || f.rfc || f.razonSocial);

      if (parsed.length === 0) {
        toastAdvertencia({
          title: "Sin filas válidas",
          message: "Revisa que existan las columnas Número de proveedor / RFC / Razón social (usa la plantilla).",
        });
        return;
      }
      setFilas(parsed);
      validar.mutate(parsed);
    } catch {
      toastAdvertencia({ title: "No se pudo leer el archivo", message: "¿Es un .xlsx/.xls válido?" });
    }
  }

  const aImportar = preview ? preview.creados + preview.actualizados : 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            leftSection={<IconFileSpreadsheet size={14} />}
            rightSection={<IconChevronDown size={14} />}
            loading={validar.isPending || importar.isPending}
          >
            Excel
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            <IconFileImport size={16} stroke={1.7} />
            Importar datos en Excel
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={descargarPlantilla}>
            <IconDownload size={16} stroke={1.7} />
            Descargar plantilla
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => void onArchivo(e)}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden="true"
      />

      <Dialog open={preview !== null} onOpenChange={(o) => !o && cerrar()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Vista previa de importación</DialogTitle>
          </DialogHeader>
          {preview && (
            <Stack gap="sm">
              <Group gap="xs">
                <Badge color="exito">{preview.creados} a crear</Badge>
                <Badge color="info">{preview.actualizados} a actualizar</Badge>
                <Badge color={preview.omitidos > 0 ? "peligro" : "slate"}>{preview.omitidos} a omitir</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {aImportar > 0
                  ? "Columna Tipo: E = extranjero (RFC libre, sin CLABE), N o vacío = nacional. Al actualizar, el RFC y el tipo no cambian (son inmutables) · solo la razón social y los datos bancarios. Las filas omitidas se ignoran."
                  : "Ninguna fila es importable (revisa los motivos). Corrige el archivo y vuelve a intentar."}
              </Text>
              <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
                <Table verticalSpacing="xs" horizontalSpacing="sm">
                  <Thead>
                    <Tr>
                      <Th>#</Th>
                      <Th>Número</Th>
                      <Th>Tipo</Th>
                      <Th>RFC</Th>
                      <Th>Razón social</Th>
                      <Th>Acción</Th>
                      <Th>Motivo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {preview.detalle.map((f) => (
                      <FilaPreview key={`${f.renglon}-${f.numeroProveedor}`} fila={f} />
                    ))}
                  </Tbody>
                </Table>
              </div>
            </Stack>
          )}
          <DialogFooter>
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button
              loading={importar.isPending}
              disabled={aImportar === 0}
              onClick={() => importar.mutate(filas, { onSuccess: cerrar })}
            >
              Confirmar importación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const COLOR_ESTADO: Record<PreviewFilaProveedor["estado"], string> = {
  Crear: "exito",
  Actualizar: "info",
  Omitir: "peligro",
};

function FilaPreview({ fila }: { fila: PreviewFilaProveedor }) {
  return (
    <Tr>
      <Td className="font-mono">{fila.renglon}</Td>
      <Td>
        <Text size="xs" ff="mono">
          {fila.numeroProveedor || "—"}
        </Text>
      </Td>
      <Td>
        <Badge size="sm" variant="light" color={fila.tipo === "Extranjero" ? "slate" : "multiva"}>
          {fila.tipo}
        </Badge>
      </Td>
      <Td>
        <Text size="xs" ff="mono">
          {fila.rfc || "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{fila.razonSocial || "—"}</Text>
      </Td>
      <Td>
        <Badge size="sm" color={COLOR_ESTADO[fila.estado]}>
          {fila.estado}
        </Badge>
      </Td>
      <Td>
        <Text size="xs" c={fila.motivo ? "peligro" : "dimmed"}>
          {fila.motivo ?? "—"}
        </Text>
      </Td>
    </Tr>
  );
}
